import { useState, useCallback } from 'react';
import { Card, CardContent, Button, Dialog, ChevronDownIcon, ChevronUpIcon } from '@/components/ui';
import { HowToUseTipsEditor } from './HowToUseTipsEditor';
import { TierBoundsEditor, type EditableTier } from './TierBoundsEditor';
import { CanonicalValuesEditor } from './CanonicalValuesEditor';
import { MetricPreviewModal } from './MetricPreviewModal';
import { MetricVersionHistory } from './MetricVersionHistory';
import { metricDefinitionsApi, type UpdateMetricDefinitionPayload } from '@/services/metric-definitions-api';
import { useMetricDefinitions } from '@/hooks/use-metric-definition';
import { validateTierSet, type IMetricDefinition, type IHowToUseTip, type IMetricTier } from '@skystone/shared';

// ---------------------------------------------------------------------------
// Form state
// ---------------------------------------------------------------------------

interface FormState {
  name: string;
  tagline: string;
  definition: string;
  /**
   * Plain-language "how it's calculated" text. Empty string means "not set"
   * (stored as NULL server-side, no Management View tooltip).
   */
  calculation: string;
  /**
   * Plain-language one-line caveat ("Watch out"). Empty string means "not set"
   * (stored as NULL server-side, no catalogue callout).
   */
  watchOut: string;
  howToUseTips: IHowToUseTip[];
  tiers: EditableTier[];
  /**
   * Mirrors `IMetricDefinition.canonicalValues`. `undefined` means the metric
   * does not use the field — the canonical-values section does not render.
   * `[]` is a valid saved state (cards then render the empty state).
   */
  canonicalValues: string[] | undefined;
}

function initForm(def: IMetricDefinition): FormState {
  return {
    name: def.name,
    tagline: def.tagline,
    definition: def.definition,
    calculation: def.calculation ?? '',
    watchOut: def.watchOut ?? '',
    howToUseTips: [...def.howToUseTips],
    tiers: def.tiers.map((t) => ({
      tierId: t.tierId,
      tierOrder: t.tierOrder,
      label: t.label,
      lowerBound: t.lowerBound,
      upperBound: t.upperBound,
      colorToken: t.colorToken,
      description: t.description,
    })),
    canonicalValues: def.canonicalValues ? [...def.canonicalValues] : undefined,
  };
}

function validateForm(form: FormState, def: IMetricDefinition): string | null {
  if (form.name.trim().length === 0) return 'Name is required';
  if (form.tagline.trim().length === 0) return 'Tagline is required';
  if (form.definition.trim().length === 0) return 'Definition is required';
  if (form.howToUseTips.length === 0) return 'At least one tip is required';
  if (form.howToUseTips.some((t) => t.text.trim().length === 0)) return 'All tips must have text';
  if (form.canonicalValues?.some((v) => v.trim().length === 0)) {
    return 'Canonical values must not be empty';
  }
  if (form.tiers.length === 0) return null; // throughput — no tiers to validate

  try {
    const fullTiers: IMetricTier[] = form.tiers.map((t) => {
      const orig = def.tiers.find((o) => o.tierId === t.tierId);
      return {
        ...t,
        lowerBoundInclusive: orig?.lowerBoundInclusive ?? false,
        upperBoundInclusive: orig?.upperBoundInclusive ?? false,
      };
    });
    validateTierSet({ metricKey: def.metricKey, direction: def.direction, tiers: fullTiers });
    return null;
  } catch (error) {
    return error instanceof Error ? error.message : 'Invalid tier configuration';
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface MetricDefinitionEditorProps {
  definition: IMetricDefinition;
  isExpanded: boolean;
  onToggle: () => void;
}

export function MetricDefinitionEditor({ definition, isExpanded, onToggle }: Readonly<MetricDefinitionEditorProps>) {
  const { refetch } = useMetricDefinitions();
  const [form, setForm] = useState<FormState>(() => initForm(definition));
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const idleSaveLabel = saveSuccess ? 'Saved!' : 'Save';
  const [error, setError] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  const validationError = validateForm(form, definition);
  const isValid = validationError === null;

  function formTiersAsFull(): IMetricTier[] {
    return form.tiers.map((t) => {
      const orig = definition.tiers.find((o) => o.tierId === t.tierId);
      return {
        ...t,
        lowerBoundInclusive: orig?.lowerBoundInclusive ?? false,
        upperBoundInclusive: orig?.upperBoundInclusive ?? false,
      };
    });
  }

  // Reset form when definition changes (e.g. after refetch)
  const resetForm = useCallback(() => {
    setForm(initForm(definition));
    setError(null);
    setSaveSuccess(false);
  }, [definition]);

  async function handleSave() {
    setSaving(true);
    setError(null);
    setSaveSuccess(false);
    try {
      const payload: UpdateMetricDefinitionPayload = {
        name: form.name,
        tagline: form.tagline,
        definition: form.definition,
        calculation: form.calculation,
        watchOut: form.watchOut,
        howToUseTips: form.howToUseTips.map((t, i) => ({ order: i + 1, text: t.text })),
        tiers: form.tiers,
        // Trim each entry to match the backend zod transform; only include
        // when the metric uses the field so non-governance saves stay
        // byte-identical to the pre-PRODOPSAI-376 payload.
        ...(form.canonicalValues === undefined
          ? {}
          : { canonicalValues: form.canonicalValues.map((v) => v.trim()) }),
      };
      await metricDefinitionsApi.update(definition.metricKey, payload);
      refetch();
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Save failed';
      setError(msg);
    } finally {
      setSaving(false);
    }
  }

  async function handleReset() {
    setShowResetConfirm(false);
    setSaving(true);
    setError(null);
    try {
      await metricDefinitionsApi.reset(definition.metricKey);
      refetch();
      resetForm();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Reset failed';
      setError(msg);
    } finally {
      setSaving(false);
    }
  }

  // After a restore the API returns the now-live definition; sync the form to
  // it directly (race-free) and refresh the catalogue/other consumers.
  function handleRestored(restored: IMetricDefinition) {
    setForm(initForm(restored));
    setError(null);
    setSaveSuccess(false);
    refetch();
  }

  function buildPreviewDefinition(): IMetricDefinition {
    const fullTiers: IMetricTier[] = form.tiers.map((t) => {
      const orig = definition.tiers.find((o) => o.tierId === t.tierId);
      return {
        ...t,
        lowerBoundInclusive: orig?.lowerBoundInclusive ?? false,
        upperBoundInclusive: orig?.upperBoundInclusive ?? false,
      };
    });
    return { ...definition, ...form, howToUseTips: form.howToUseTips.map((t, i) => ({ order: i + 1, text: t.text })), tiers: fullTiers };
  }

  return (
    <Card className="overflow-hidden">
      <button
        type="button"
        className="flex w-full items-center justify-between p-4 text-left hover:bg-muted/50"
        onClick={onToggle}
        aria-expanded={isExpanded}
      >
        <div className="flex items-center gap-3">
          <span className="text-xl" aria-hidden="true">{definition.icon}</span>
          <div>
            <p className="text-sm font-semibold text-foreground">{definition.name}</p>
            <p className="text-xs text-muted-foreground">{definition.sourceLabel} · {definition.unit}</p>
          </div>
        </div>
        {isExpanded ? <ChevronUpIcon className="h-4 w-4 text-muted-foreground" /> : <ChevronDownIcon className="h-4 w-4 text-muted-foreground" />}
      </button>

      {isExpanded && (
        <CardContent className="border-t border-border p-4">
          {error && <p className="mb-3 text-xs text-error" role="alert">{error}</p>}

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {/* Left: Prose */}
            <div className="space-y-3">
              <div>
                <label>
                  <span className="mb-1 block text-xs font-medium text-muted-foreground">Name</span>
                  <input
                    className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm focus:border-primary focus:outline-none"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                  />
                </label>
              </div>
              <div>
                <label>
                  <span className="mb-1 block text-xs font-medium text-muted-foreground">Tagline</span>
                  <input
                    className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm focus:border-primary focus:outline-none"
                    value={form.tagline}
                    onChange={(e) => setForm({ ...form, tagline: e.target.value })}
                  />
                </label>
              </div>
              <div>
                <label>
                  <span className="mb-1 block text-xs font-medium text-muted-foreground">Definition</span>
                  <textarea
                    className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm leading-relaxed focus:border-primary focus:outline-none"
                    rows={4}
                    value={form.definition}
                    onChange={(e) => setForm({ ...form, definition: e.target.value })}
                  />
                </label>
              </div>
              <div>
                <label>
                  <span className="mb-1 block text-xs font-medium text-muted-foreground">How it&apos;s calculated</span>
                  <textarea
                    className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm leading-relaxed focus:border-primary focus:outline-none"
                    rows={6}
                    value={form.calculation}
                    onChange={(e) => setForm({ ...form, calculation: e.target.value })}
                    placeholder="Plain-language explanation of how this metric is calculated…"
                  />
                </label>
                <p className="mt-1 text-[11px] text-muted-foreground">
                  Shown as the ⓘ tooltip on the Organisational View → Quality Control matrix cards. Leave blank to hide it.
                </p>
              </div>
              <div>
                <label>
                  <span className="mb-1 block text-xs font-medium text-muted-foreground">Watch out</span>
                  <textarea
                    className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm leading-relaxed focus:border-primary focus:outline-none"
                    rows={2}
                    value={form.watchOut}
                    onChange={(e) => setForm({ ...form, watchOut: e.target.value })}
                    placeholder="One-line caveat shown as the Watch-out callout…"
                  />
                </label>
                <p className="mt-1 text-[11px] text-muted-foreground">
                  Shown as the ⚠ &ldquo;Watch out&rdquo; callout on the Definitions Catalogue card. Leave blank to hide it.
                </p>
              </div>
              <div>
                <label>
                  <span className="mb-1 block text-xs font-medium text-muted-foreground">Source</span>
                  <input
                    className="w-full rounded-md border border-border bg-muted px-2 py-1.5 text-sm text-muted-foreground"
                    value={definition.sourceLabel}
                    disabled
                    aria-label="Source label (read-only)"
                  />
                </label>
              </div>
              <HowToUseTipsEditor
                tips={form.howToUseTips}
                onChange={(tips) => setForm({ ...form, howToUseTips: tips })}
              />
            </div>

            {/* Right: Tiers */}
            <div>
              <TierBoundsEditor
                tiers={formTiersAsFull()}
                unit={definition.unit}
                onChange={(tiers) => setForm({ ...form, tiers })}
                validationError={validationError}
              />
            </div>
          </div>

          {/* Canonical values (governance metrics only — drives "non-canonical"
              breakdowns on Workflow Hygiene cards). Section is conditional on
              the field's presence rather than a hard-coded metricKey list. */}
          {form.canonicalValues !== undefined && (
            <div className="mt-4 border-t border-border pt-3">
              <CanonicalValuesEditor
                values={form.canonicalValues}
                onChange={(canonicalValues) => setForm({ ...form, canonicalValues })}
              />
            </div>
          )}

          {/* Footer */}
          <div className="mt-4 flex items-center gap-2 border-t border-border pt-3">
            <Button
              size="sm"
              onClick={handleSave}
              disabled={saving || !isValid}
            >
              {saving ? 'Saving...' : idleSaveLabel}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setShowResetConfirm(true)} disabled={saving}>
              Reset to defaults
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setShowPreview(true)}>
              Preview
            </Button>
          </div>

          {/* Version history + restore (PRODOPSAI-652) */}
          <MetricVersionHistory definition={definition} onRestored={handleRestored} />

          {/* Reset confirmation */}
          <Dialog open={showResetConfirm} onClose={() => setShowResetConfirm(false)} title="Reset to defaults?">
            <p className="text-sm text-muted-foreground">
              This will restore <strong>{definition.name}</strong> to its original definition, tagline, tips and tier thresholds. This cannot be undone.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setShowResetConfirm(false)}>Cancel</Button>
              <Button size="sm" onClick={handleReset}>Reset</Button>
            </div>
          </Dialog>

          {/* Preview modal */}
          <MetricPreviewModal
            open={showPreview}
            onClose={() => setShowPreview(false)}
            definition={buildPreviewDefinition()}
          />
        </CardContent>
      )}
    </Card>
  );
}
